/**
 * Zuora Worker Health panel — the RAMP GATE for the reconcile stage.
 *
 * Lives at the top of /admin/zuora-reconciliation because that is where an
 * operator already goes to decide whether the pipeline is safe to push harder.
 * Before this panel existed nothing in the app read zuora_worker_health(), so
 * the only ramp signal lived in a function nobody could see — which is exactly
 * why the reconcile ramp stayed blocked after the phone-fill incident.
 *
 * The single question this answers is "is it safe to ramp right now?". The
 * server answers it as one boolean (`alarm`), but a bare red light is useless
 * at 2am, so the panel also names WHICH of the five conditions tripped and
 * shows the numbers behind each one.
 *
 * Authorization is server-side: the RPC is SECURITY DEFINER and refuses anyone
 * who is not service_role or an ACTIVE admin/superadmin profile. The route
 * guard around this page mirrors that, but the function is the enforcement.
 */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, formatDistanceToNow } from "date-fns";
import LoadingSpinner from "../common/LoadingSpinner";
import {
  getZuoraWorkerHealth,
  WORKER_HEALTH_WINDOWS,
  type ZuoraWorkerHealth,
} from "../../services/zuoraReconciliation.service";

/** Matches the page's own cadence; the worker cron fires every minute. */
const REFRESH_MS = 30_000;

/**
 * The five conditions the server ORs into `alarm`, in the order an operator
 * should read them: "did it run at all" before "did the runs go well".
 *
 * Each row is named for what it VERIFIES, not for the fault it detects. The
 * server reports these as fault flags (no_runs, heartbeat_stale, …), and
 * mirroring that naming into the UI made a passing row read as an assertion of
 * the fault — "Heartbeat stale ✓ ok — the worker has stopped". The label states
 * the healthy property and `trips` states the rule, so the row reads correctly
 * in BOTH states.
 *
 * `trips` quotes the server's actual thresholds (from _zuora_worker_health_raw)
 * so the panel never implies a rule the function doesn't use. `faultPhrase` is
 * the noun phrase used when summarising which checks tripped.
 */
const ALARM_CONDITIONS: {
  key: keyof Pick<
    ZuoraWorkerHealth,
    | "no_runs"
    | "heartbeat_stale"
    | "silent_stall"
    | "any_failed"
    | "degraded_sustained"
  >;
  label: string;
  trips: string;
  faultPhrase: string;
}[] = [
  {
    key: "no_runs",
    label: "Worker ran in window",
    trips: "Trips when the worker logged zero runs in the selected window.",
    faultPhrase: "no runs in the window",
  },
  {
    key: "heartbeat_stale",
    label: "Heartbeat fresh",
    trips:
      "Trips when the last run was over 5 minutes ago, or the worker has never run at all. The cron fires every minute. Measured across all time, not just the selected window.",
    faultPhrase: "stale heartbeat",
  },
  {
    key: "silent_stall",
    label: "Runs are succeeding",
    trips:
      "Trips when runs are being logged in the window but none of them finish healthy.",
    faultPhrase: "no successful runs",
  },
  {
    key: "any_failed",
    label: "No failed runs",
    trips: "Trips when at least one run in the window ended in a failed state.",
    faultPhrase: "failed runs",
  },
  {
    key: "degraded_sustained",
    label: "Success rate holding",
    trips:
      "Trips when there are at least 5 runs in the window and fewer than 80% of them are healthy.",
    faultPhrase: "sustained degradation",
  },
];

function fmtTs(ts: string | null): string {
  if (!ts) {
    return "—";
  }
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "—" : format(d, "MMM d, yyyy h:mm:ss a");
}

function fmtAge(ts: string | null): string {
  if (!ts) {
    return "never run";
  }
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "—" : `${formatDistanceToNow(d)} ago`;
}

/**
 * ok_ratio is null when runs = 0 — there is no denominator. Rendering that as
 * "0%" would read as "everything failed" when the truth is "nothing ran", so
 * the two cases stay visually distinct.
 */
function fmtRatio(ratio: number | null): string {
  return ratio === null ? "—" : `${(ratio * 100).toFixed(1)}%`;
}

function Metric({
  label,
  value,
  tone = "default",
  hint,
}: {
  label: string;
  value: number | string;
  tone?: "default" | "warn" | "danger" | "ok";
  hint?: string;
}) {
  const toneClass =
    tone === "danger"
      ? "text-red-600"
      : tone === "warn"
        ? "text-yellow-600"
        : tone === "ok"
          ? "text-green-600"
          : "text-gray-900";
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        {label}
      </div>
      <div className={`mt-1 text-xl font-bold ${toneClass}`}>{value}</div>
      {hint && <div className="mt-0.5 text-xs text-gray-400">{hint}</div>}
    </div>
  );
}

const ZuoraWorkerHealthPanel: React.FC = () => {
  const [windowInterval, setWindowInterval] = useState<string>("1 hour");

  const healthQ = useQuery({
    queryKey: ["zuora-recon", "worker-health", windowInterval],
    queryFn: () => getZuoraWorkerHealth(windowInterval),
    refetchInterval: REFRESH_MS,
  });

  const h = healthQ.data;
  const tripped = h ? ALARM_CONDITIONS.filter((c) => h[c.key]) : [];

  return (
    <section className="mb-8">
      <div className="flex items-start justify-between mb-3 gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Worker Health</h2>
          <p className="text-sm text-gray-500">
            Whether the reconcile worker is healthy enough to ramp. Checked
            before raising throughput.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label
            htmlFor="worker-health-window"
            className="text-xs font-medium text-gray-500 uppercase tracking-wide"
          >
            Window
          </label>
          <select
            id="worker-health-window"
            value={windowInterval}
            onChange={(e) => setWindowInterval(e.target.value)}
            className="border border-gray-300 rounded-md text-sm py-1.5 px-2 bg-white"
          >
            {WORKER_HEALTH_WINDOWS.map((w) => (
              <option key={w.value} value={w.value}>
                {w.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {healthQ.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Could not read worker health:{" "}
          {(healthQ.error as Error).message ?? "unknown error"}
        </div>
      )}

      {healthQ.isLoading && !h && <LoadingSpinner />}

      {h && (
        <>
          {/* Verdict — the one line the ramp decision actually turns on. */}
          <div
            className={`rounded-lg border p-4 mb-4 ${
              h.alarm
                ? "border-red-300 bg-red-50"
                : "border-green-300 bg-green-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <span
                className={`inline-block h-3 w-3 rounded-full ${
                  h.alarm ? "bg-red-600" : "bg-green-600"
                }`}
                aria-hidden="true"
              />
              <span
                className={`font-semibold ${
                  h.alarm ? "text-red-800" : "text-green-800"
                }`}
              >
                {h.alarm ? "Not safe to ramp" : "Healthy — safe to ramp"}
              </span>
            </div>
            <p
              className={`mt-1 text-sm ${
                h.alarm ? "text-red-700" : "text-green-700"
              }`}
            >
              {h.alarm
                ? `${tripped.length} of 5 checks tripped: ${tripped
                    .map((c) => c.faultPhrase)
                    .join(", ")}.`
                : "All 5 checks passed for the selected window."}
            </p>
          </div>

          {/* Per-condition detail, so a red light says which rule fired. */}
          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100 mb-4">
            {ALARM_CONDITIONS.map((c) => {
              const failing = h[c.key];
              return (
                <div key={c.key} className="flex items-start gap-3 p-3">
                  <span
                    className={`mt-0.5 shrink-0 inline-flex items-center justify-center h-5 w-5 rounded-full text-xs font-bold ${
                      failing
                        ? "bg-red-100 text-red-700"
                        : "bg-green-100 text-green-700"
                    }`}
                    aria-hidden="true"
                  >
                    {failing ? "!" : "✓"}
                  </span>
                  <div className="min-w-0">
                    <div
                      className={`text-sm font-medium ${
                        failing ? "text-red-800" : "text-gray-900"
                      }`}
                    >
                      {c.label}
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        {failing ? "tripped" : "ok"}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">{c.trips}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Supporting numbers. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Metric
              label="Runs in window"
              value={h.runs}
              tone={h.runs === 0 ? "danger" : "default"}
            />
            <Metric
              label="Healthy"
              value={h.ok}
              tone={h.runs > 0 && h.ok === 0 ? "danger" : "ok"}
              hint={`${fmtRatio(h.ok_ratio)} of runs`}
            />
            <Metric
              label="Degraded"
              value={h.degraded}
              tone={h.degraded ? "warn" : "default"}
            />
            <Metric
              label="Failed"
              value={h.failed}
              tone={h.failed ? "danger" : "default"}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric
              label="Last run"
              value={fmtAge(h.last_run_at)}
              tone={h.heartbeat_stale ? "danger" : "default"}
              hint={fmtTs(h.last_run_at)}
            />
            <Metric
              label="Claim failures"
              value={h.claim_failed}
              tone={h.claim_failed ? "danger" : "default"}
              hint="could not claim work"
            />
            <Metric
              label="Reconcile failures"
              value={h.reconcile_failed}
              tone={h.reconcile_failed ? "danger" : "default"}
              hint={`${h.reconcile_stopped_early} stopped early`}
            />
            <Metric
              label="Queue pending"
              value={h.queue_pending.toLocaleString()}
              tone={h.queue_pending ? "warn" : "default"}
              hint="awaiting reconcile"
            />
          </div>
        </>
      )}
    </section>
  );
};

export default ZuoraWorkerHealthPanel;
